<template>
    <div class="invest" :style="bannerStyles" ref="investmentDiv">
        <div class="margin-general">
            <h3 v-if="label" v-html="formatContent(label, $t, localPath)"></h3>
            <div v-if="cont_descript && text_descript">
                <p class="heading-h4">{{ $t(text_descript) }}</p>
            </div>

            <div v-if="cont_descript2 && text_descript2_keys && text_descript2_keys.length > 0">
                <p
                    v-for="(descKey, index) in text_descript2_keys"
                    :key="`desc2-${index}`"
                    class="body-6"
                    v-html="formatContent(descKey, $t, localPath)"
                >
                </p>
            </div>

            <div class="buttons" v-if="buttonConfigs && buttonConfigs.length">
                <template v-for="(btn, idx) in buttonConfigs" :key="'btn-'+idx">
                    <NuxtLink v-if="btn.type === 'internal' && btn.to" :to="localPath(btn.to)">
                        <button :class="btn.class || 'primary-button-medium'">
                        {{ $t(btn.textKey) }}
                        </button>
                    </NuxtLink>
                    <a v-else-if="btn.type === 'external' && btn.href" :href="btn.href" target="_blank" rel="noopener noreferrer">
                        <button :class="btn.class || 'primary-button-medium'">
                        {{ $t(btn.textKey) }}
                        </button>
                    </a>
                    <button v-else-if="btn.type === 'action' && typeof btn.onClick === 'function'" @click="btn.onClick" :class="btn.class || 'primary-button-medium'">
                        {{ $t(btn.textKey) }}
                    </button>
                    <button v-else-if="btn.type === 'scroll' && btn.targetId" @click="() => scrollToSection(btn.targetId)" :class="btn.class || 'primary-button-medium'">
                        {{ $t(btn.textKey) }}
                    </button>
                    <!-- Botón especial para links por sistema operativo -->
                    <a v-else-if="btn.type === 'os-link' && selectedOSLinks[idx]" :href="selectedOSLinks[idx]" target="_blank" rel="noopener noreferrer">
                        <button :class="btn.class || 'primary-button-medium'">
                        {{ $t(btn.textKey) }}
                        </button>
                    </a>
                    <button v-else :class="btn.class || 'primary-button-medium'">
                        {{ $t(btn.textKey) }}
                    </button>
                </template>
            </div>
        </div>
    </div>
</template>
  
<script setup>
    import { ref, computed, onMounted, onUnmounted } from "vue"; 
    import { useLocalePath } from '#i18n'; 
    import { formatContent } from '~/utils/formatContent';
    import { useImage } from '#imports';
    
    const nuxtImg = useImage();
    const localPath = useLocalePath(); 
    
    const investmentDiv = ref(null);
    const isImageVisible = ref(false);

    // NUEVO: Variable reactiva para el enlace os-link
    const selectedOSLinks = ref({});

    const props = defineProps({
        label: { 
            type: String,
            default: ''
        },
        imgBannerGradient: { 
            type: String,
            default: ''
        },
        cont_descript: Boolean, 
        text_descript: { 
            type: String,
            default: ''
        },
        cont_descript2: Boolean, 
        text_descript2_keys: {
        type: Array, 
        default: () => [],
        validator: (value) => value.every(item => typeof item === 'string') 
        },
        buttonConfigs: {
        type: Array,
        default: () => []
        }
    });

    // Lógica igual a prefooter para detectar el SO y guardar el link correcto para cada botón os-link
    function getOS() {
        const userAgent = window.navigator.userAgent;
        if (/Windows/i.test(userAgent)) {
            return 'windows';
        } else if (/Android/i.test(userAgent)) {
            return 'android';
        } else if (/Mac/i.test(userAgent)) {
            return 'mac';
        } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
            return 'ios';
        }
        return null;
    }
    
    const scrollToSection = (targetId = 'formulario') => {
        const element = document.getElementById(targetId);
        if (!element) {
        console.warn(`Scroll target element with ID "${targetId}" not found.`);
        return;
        }
    
        // Lee todas las propiedades necesarias antes de la animación
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        const offset = 70;
        const targetPosition = elementPosition - offset;
    
        const startPosition = window.scrollY;
        const distance = targetPosition - startPosition;
        const duration = 2000; 
        let startTime = null;
    
        const easeInOutCubic = (t) => {
        return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
        };
    
        const animationStep = (currentTime) => {
        if (startTime === null) {
            startTime = currentTime;
        }
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);
        const easedProgress = easeInOutCubic(progress);
    
        // Solo escritura aquí
        window.scrollTo(0, startPosition + distance * easedProgress);
    
        if (timeElapsed < duration) {
            requestAnimationFrame(animationStep);
        }
        };
    
        requestAnimationFrame(animationStep);
    };
    
    const optimizedBackgroundImageUrl = computed(() => {
        if (!props.imgBannerGradient) {
        return 'none'; 
        }
        return nuxtImg(props.imgBannerGradient, {
        quality: 80, 
        format: 'webp', 
        });
    });
    
    const bannerStyles = computed(() => {
        if (!props.imgBannerGradient) {
            return null;
        }
        return {
            backgroundImage: isImageVisible.value ? `url('${optimizedBackgroundImageUrl.value}')` : 'none',
        };
    });
    
    onMounted(() => {
        // Para cada botón os-link, calcula el enlace correcto y lo guarda en selectedOSLinks
        props.buttonConfigs.forEach((btn, idx) => {
            if (btn.type === 'os-link' && btn.osLinks) {
                const os = getOS();
                selectedOSLinks.value[idx] = btn.osLinks[os] || null;
            }
        });
        const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
            if (entry.isIntersecting) {
                isImageVisible.value = true;
                observer.disconnect();
            }
            });
        },
        { rootMargin: '100px' }
        );
    
        if (investmentDiv.value) {
        observer.observe(investmentDiv.value);
        }
    });
    
    onUnmounted(() => {
        if (investmentDiv.value) {
        const observer = new IntersectionObserver(() => {});
        observer.disconnect();
        }
    }); 
</script>
  